/**
 * The catch-all value of a location criterion's Disqualify bucket.
 *
 * A rubric's HQ / subsidiary / person location criterion lists the locations a
 * lead is scored for. Everything else used to be answered with the scoring
 * engine's "known, but not in the list" sentinel (-2), which scores nothing but
 * never disqualifies — so a lead sitting far outside the target market silently
 * placed in a priority bucket instead of being rejected.
 *
 * The generated rubric therefore always carries one 0-point item holding this
 * token, making "we do not sell there" explicit, visible in the setup, and
 * removable by the user.
 *
 * It is a sentinel, not a place: the scoring prompts render it as a catch-all
 * sentence rather than listing it among the countries, and every consumer that
 * turns rubric countries into a real search filter must drop it (there is no
 * such country code).
 */
export const REST_OF_THE_WORLD = "Rest of the world";

/** Whether a rubric country value is the catch-all rather than a real location. */
export const isRestOfTheWorld = (country: string): boolean =>
	country.trim().toLowerCase() === REST_OF_THE_WORLD.toLowerCase();

/** The real locations of a rubric item — the catch-all removed. */
export const withoutRestOfTheWorld = (countries: string[] = []): string[] =>
	countries.filter((country) => !isRestOfTheWorld(country));

/**
 * The region tokens a location criterion may carry besides plain countries
 * (PREC-4055).
 *
 * The scoring engine already understands these: its location prompts tell the
 * model to check whether a country is part of a listed region, and the API's
 * region table expands each of them to member countries. The settings editor
 * offers exactly this list, and the API pins the two in step with a spec —
 * every token here must expand in `expandRegionToken`.
 */
export const LEAD_SCORING_REGIONS = [
	"DACH",
	"Benelux",
	"Nordics",
	"European Union",
	"Europe",
	"CEE",
	"UK",
	"EMEA",
	"MENA",
	"GCC",
	"North America",
	"South America",
	"LATAM",
	"Africa",
	"Asia",
	"APAC",
	"SEA",
	"ANZ",
	"Oceania",
] as const;

export type LeadScoringRegion = (typeof LEAD_SCORING_REGIONS)[number];

/** Whether a rubric location value is one of the offered region tokens. */
export const isLeadScoringRegion = (value: string): boolean =>
	LEAD_SCORING_REGIONS.some((region) => region.toLowerCase() === value.trim().toLowerCase());
